// "Edit in Wixel" on a PHONE or TABLET browser first asks where to edit: the Wixel app or the browser editor,
// which is the desktop web editor and was the only answer before. Desktop web is unchanged — the item stays
// a plain anchor that opens the editor in a new tab.
//
// "Get the Wixel app" is the Wixel app's universal link (`wixelAppLink`, on `apps.wixel.com`) when the editor
// URL has one: a tap on it opens the installed app on that asset, and without the app the link redirects to
// the right store. With no app link (an editor URL of neither known form) it is the OS's store page. A browser
// cannot ask whether the app is installed, so web keeps the sheet where the native apps first try the app
// silently and show the sheet only when it did not open.
//
// This module is THE one place that decides it, for every host that offers Edit in Wixel (`buildFileActions`'
// `edit-wixel` item, which reaches DocViewerModal, the Files rows, ChatFilesPanel and FileDetailBody, and
// `WixelDocView`'s own Edit button). A host never builds an editor link of its own, so none can skip the sheet.
// The same sheet, keys, app link and store listings as the native apps (`WixelEditChoice` on iOS and
// Android); web shows the sheet only where the OS has a Wixel app to offer.
//
// The decision is a UA read, not a width read: a narrow desktop window is still a desktop with no app to get,
// and an iPad at full width is still iPadOS. That keeps `buildFileActions` presentation-blind.
//
// The sheet itself (`components/symphony/WixelEditChoiceSheet.tsx`) is mounted once in `_app.tsx`, and an
// action raises it through `requestWixelEditChoice` below rather than through React context, because
// `buildFileActions` is a plain function with no hooks and its callers should not each have to wire it.

import { WIXEL_ANDROID_URL, WIXEL_IOS_URL } from './app-store-links';
import { wixelAppLink } from './wixel-link';

export type MobileOs = 'ios' | 'android';

/** The phone/tablet OS a browser runs on, or null for a desktop.
 *
 *  ⚠️ iPadOS Safari asks for the desktop site by default and reports a MAC user agent
 *  (`Macintosh; Intel Mac OS X …`). No Mac has a touch screen, so a Mac UA with more than one touch point
 *  is an iPad. */
export function mobileOsOf(userAgent: string, maxTouchPoints = 0): MobileOs | null {
  if (/Android/i.test(userAgent)) return 'android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return 'ios';
  return null;
}

/** This browser's phone/tablet OS, or null on a desktop and during SSR. */
export function currentMobileOs(): MobileOs | null {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') return null;
  return mobileOsOf(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

/** The Wixel app's store page for an OS: the App Store for iOS and iPadOS, Play for Android. */
export function wixelStoreUrl(os: MobileOs): string {
  return os === 'ios' ? WIXEL_IOS_URL : WIXEL_ANDROID_URL;
}

/** Where "Get the Wixel app" points for an editor URL: its app link, else the store page for this OS. */
export function wixelGetAppUrl(editorUrl: string, os: MobileOs): string {
  return wixelAppLink(editorUrl) ?? wixelStoreUrl(os);
}

export interface WixelEditChoiceRequest {
  editorUrl: string;
  /** The app link, or the store page when the editor URL has none (`wixelGetAppUrl`). */
  getAppUrl: string;
}

type Listener = (request: WixelEditChoiceRequest) => void;

let listener: Listener | null = null;

/** Called by the mounted sheet. One sheet at a time, so a later subscriber replaces an earlier one. */
export function subscribeWixelEditChoice(next: Listener): () => void {
  listener = next;
  return () => {
    if (listener === next) listener = null;
  };
}

/** Raises the choice sheet for one editor URL. With no sheet mounted (which `_app.tsx` rules out) it opens
 *  the editor as before, so the action is never a dead tap. */
export function requestWixelEditChoice(editorUrl: string, os: MobileOs): void {
  if (listener) {
    listener({ editorUrl, getAppUrl: wixelGetAppUrl(editorUrl, os) });
    return;
  }
  window.open(editorUrl, '_blank', 'noopener,noreferrer');
}

/** How an Edit-in-Wixel control is wired: on a desktop the editor as an external anchor (a new tab, exactly
 *  as before), on a phone or tablet a handler that raises the choice sheet and carries NO href, so the
 *  editor cannot open behind it. Spread into an `OverflowMenuAction`. */
export type EditInWixelLink = { href: string; external: true; onSelect?: undefined } | { href?: undefined; onSelect: () => void };

export function editInWixelLink(editorUrl: string, os: MobileOs | null = currentMobileOs()): EditInWixelLink {
  if (!os) return { href: editorUrl, external: true };
  return { onSelect: () => requestWixelEditChoice(editorUrl, os) };
}
